import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, BookOpen, BarChart3, Settings } from 'lucide-react';

const navItems = [
  { label: 'Today', icon: Home, path: '/dashboard' },
  { label: 'Insights', icon: BarChart3, path: '/analytics' },
  { label: 'Log', icon: BookOpen, path: '/entries' },
  { label: 'Settings', icon: Settings, path: '/settings' },
];

interface BottomNavProps {
  blurred?: boolean;
}

const BottomNav: React.FC<BottomNavProps> = ({ blurred = false }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Hide FAB button during check-in flow
  const isCheckInFlow = location.pathname.includes('/check-in');

  const handleNavigation = (path: string, label: string) => {
    navigate(path);
    // Scroll to top when navigating to Today
    if (label === 'Today') {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <nav className={`fixed bottom-0 left-0 w-full border-t border-gray-200 flex justify-around items-center h-20 md:h-16 z-50 md:hidden ${blurred ? 'bg-white/80 backdrop-blur-lg opacity-100' : 'bg-white'}`}>
      {navItems.map(({ label, icon: Icon, path }) => {
        const isActive = location.pathname.startsWith(path);
        return (
          <button
            key={label}
            className={`flex flex-col items-center justify-center flex-1 py-3 md:py-2 min-h-[48px] focus:outline-none ${isActive ? 'text-rose-600' : 'text-gray-400'} transition-colors`}
            onClick={() => handleNavigation(path, label)}
            tabIndex={blurred ? -1 : 0}
            aria-disabled={blurred}
            disabled={blurred}
          >
            <Icon size={28} className="md:w-[26px] md:h-[26px]" />
            <span className="text-xs mt-1 font-medium">{label}</span>
          </button>
        );
      })}
      {/* Center action button - hide during check-in flow */}
      {!isCheckInFlow && (
        <button
          className="absolute -top-7 md:-top-6 left-1/2 transform -translate-x-1/2 bg-rose-600 text-white rounded-full p-5 md:p-4 shadow-lg border-4 border-white focus:outline-none min-w-[56px] min-h-[56px] md:min-w-[48px] md:min-h-[48px] flex items-center justify-center"
          onClick={() => navigate('/check-in-chat')}
          aria-label="New Entry"
          tabIndex={blurred ? -1 : 0}
          aria-disabled={blurred}
          disabled={blurred}
        >
          <svg width="28" height="28" fill="none" viewBox="0 0 24 24">
            <path d="M12 5v14m7-7H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}
    </nav>
  );
};

export default BottomNav; 